import React from 'react';
import classNames from 'classnames';
import { Box, IBoxProps } from '@styleseat/ui';

import { useResponsive } from '../../hooks/useResponsiveEffect';
import { ClientNavBarContainer } from '../consumer/ClientNavBar/ClientNavBarContainer';
import { APP } from '../../config';

import ConnectedSidebarContainer from '../shared/Sidebar/DeprecatedSidebarContainer';
import SidebarAnimator from '../shared/Sidebar/SidebarAnimator';
import { HeaderContainer } from '../consumer/header/HeaderContainer';
import Footer from '../shared/Footer';
import SSErrorBoundary from '../SSErrorBoundary';
import PageLoadError from '../shared/PageLoadError';
import { NAV_BAR_HEIGHT } from '../shared/BottomNavBar/BottomNavBar';
import { useIsEnabled } from '../../hooks';
import { LANDING_REDESIGN_2024_FLAG } from '../shared/homepage/HomePage/constants';

import { useShowSubscriptionFeatures } from '../../hooks/useShowSubscriptionFeatures';

const getAnimator = closeCallback => new SidebarAnimator(closeCallback);
const Sidebar = () => <ConnectedSidebarContainer getAnimator={getAnimator} />;

type ResponsiveLayoutProps = {
  topBarLoaded?: boolean;
  showClientNavBar?: boolean;
  showHeader?: boolean;
  hideFooter?: boolean;
  fitToWindow?: boolean;
  containerStyle?: IBoxProps['style'];
  renderToast?: (showClientNavBar: boolean) => React.ReactNode;
};

/**
 * Renders a layout with a navbar or client tab bar with support for a toast
 * @param children
 * @param showClientNavBar
 * @param topBarLoaded
 * @param renderToast
 * @constructor
 */
const ResponsiveLayout: React.FCWithChildren<ResponsiveLayoutProps> = ({
  children,
  showClientNavBar,
  topBarLoaded,
  showHeader = true,
  hideFooter = false,
  containerStyle,
  renderToast,
  fitToWindow,
}) => {
  const [canShowSubscriptionFeatures] = useShowSubscriptionFeatures();
  const isLandingRedesignEnabled = useIsEnabled(LANDING_REDESIGN_2024_FLAG);

  const is = useResponsive();
  const mainContentStyle = {
    marginTop: !showHeader ? 0 : undefined,
    bottom: showClientNavBar ? NAV_BAR_HEIGHT : 0,
  };

  return (
    <Box
      className={classNames(
        'sbp-state',
        'react-responsive-layout',
        {
          'app-build': APP,
          'fit-to-window': fitToWindow,
        },
      )}
    >
      <Box
        className={classNames(
          'responsive',
          'sidebar-navigation-content',
          {
            'topbar-loaded': topBarLoaded,
          },
        )}
      >
        <Sidebar />
        <Box
          maxWidth={fitToWindow || isLandingRedesignEnabled ? undefined : 1440}
          style={{
            margin: 'auto',
            ...(containerStyle as any),
            width: '100%',
          }}
          className="responsive-wrapper outside-sidebar"
        >
          {showHeader && (
            <header className="main-header">
              <HeaderContainer />
            </header>
          )}
          <Box flex={1} flexBasis="100%">
            <SSErrorBoundary
              info="ResponsiveLayout children"
              renderFallback={() => <PageLoadError />}
            >
              <Box
                className="main-content"
                alignItems="center"
                style={mainContentStyle}
              >
                {children}
                {!hideFooter && is.desktop && (
                  <Box alignSelf="flex-end" width="100%">
                    <Footer canShowSubscriptionFeatures={canShowSubscriptionFeatures} />
                  </Box>
                )}
              </Box>
            </SSErrorBoundary>
            {showClientNavBar && !is.desktop ? <ClientNavBarContainer /> : null}
            {!!renderToast && (
              <div style={{ position: 'static' }}>
                {renderToast(showClientNavBar)}
              </div>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ResponsiveLayout;
